import { BadgeVariants } from "ds/components/Badge/types";
import {
  RunExternalDependencyStatus,
  RunPolicyReceipt,
  RunReview,
  RunState,
} from "types/generated";
import { AccessResult } from "utils/access";

export enum RunHistoryEntryType {
  Comment = "comment",
  Queued = "queued",
  Ready = "ready",
  Discarded = "discarded",
  Preparing = "preparing",
  Initializing = "initializing",
  Planning = "planning",
  Unconfirmed = "unconfirmed",
  ReplanRequested = "replanRequested",
  Failed = "failed",
  Confirmed = "confirmed",
  Applying = "applying",
  Finished = "finished",
  Canceled = "canceled",
  Stopped = "stopped",
  Performing = "performing",
  Skipped = "skipped",
  Destroying = "destroying",
  PendingReview = "pendingReview",
  WorkerPool = "workerPool",
  Dependencies = "dependencies",
  TriggeredRuns = "triggeredRuns",
  ApprovalPolicies = "approvalPolicies",
  PlanPolicies = "planPolicies",
  TriggerPolicies = "triggerPolicies",
  InitializationPolicies = "initializationPolicies",
  NotificationPolicies = "notificationPolicies",
  PushPolicies = "pushPolicies",
}

export type RunShortcut = {
  id: string;
  label: string;
  code: string;
  callback: () => Promise<unknown> | Promise<void> | void;
  loading?: boolean;
  disabled?: boolean;
};

export type RunShortcuts = Array<RunShortcut>;

export enum RunElementType {
  TerminateRunAction = "terminateRunAction",
  Logs = "logs",
  AfterRunHooks = "afterRunHooks",
  ConfirmRunAction = "confirmRunAction",
  ReviewForm = "reviewForm",
  ApprovalPolicies = "approvalPolicies",
  PlanPolicies = "planPolicies",
  TriggerPolicies = "triggerPolicies",
  InitializationPolicies = "initializationPolicies",
  NotificationPolicies = "notificationPolicies",
  PushPolicies = "pushPolicies",
}

export type RunApprovalPoliciesGroup = Array<RunPolicyReceipt | RunReview>;

export enum RunApprovalPoliciesEntryType {
  Policies = "policies",
  Review = "review",
}

type RunApprovalPoliciesEntryPolicies = {
  type: RunApprovalPoliciesEntryType.Policies;
  policies: Array<RunPolicyReceipt>;
};

type RunApprovalPoliciesEntryReview = {
  type: RunApprovalPoliciesEntryType.Review;
  review: RunReview;
};

export type RunApprovalPoliciesEntryList = Array<
  RunApprovalPoliciesEntryPolicies | RunApprovalPoliciesEntryReview
>;

type RunHook = string[] | undefined;

export type RunEntryContext = {
  canReview: AccessResult;
  runHooks: {
    afterRun: RunHook;
    afterApply: RunHook;
    beforeApply: RunHook;
    afterDestroy: RunHook;
    beforeDestroy: RunHook;
    afterInit: RunHook;
    beforeInit: RunHook;
    afterPerform: RunHook;
    beforePerform: RunHook;
    afterPlan: RunHook;
    beforePlan: RunHook;
  };
  isSimpleView: boolean;
  canConfirm?: AccessResult;
  canDiscard: AccessResult;
  canPerformAgain?: AccessResult;
  stackId: string;
  runId: string;
  runTaskCommand: string | null;
  isStackDisabled: boolean;
  isModuleRun: boolean;
  canRetry: AccessResult;
  runRetryBlocker: string | null;
  canStop: AccessResult;
  canKill: AccessResult;
  isRunFinished: boolean;
  isTaskRun: boolean;
  canPromote: AccessResult;
  isProposedRun: boolean;
  runQueryToRefetch: string;
  canSkipExternalDependencies: AccessResult;
};

export type RunEntryEvaluationProps = {
  evaluationLabel: string;
  evaluationBadgeVariant: BadgeVariants;
  evaluationTimestamp: number;
};

export type RunDependenciesEntryEvaluationProps = Omit<
  RunEntryEvaluationProps,
  "evaluationBadgeVariant"
> & {
  evaluationState: RunExternalDependencyStatus | RunState;
};

export type RunPolicyEvaluation = {
  label: string;
  badgeVariant: BadgeVariants;
  timestamp: number;
  isImportant: boolean;
};
